import React, { useCallback, useEffect, useRef, useState } from 'react';

import { MediaType } from '../../types/api';
import './MediaPicker.css';

const IMG_LIMIT = 4;
const VIDEO_LIMIT = 1;

export interface PickedFile {
  id: string;
  file: File;
  kind: MediaType;
  url: string;
}

function classify(file: File): MediaType {
  const top = (file.type || '').split('/')[0];
  if (top === 'image') return 'image';
  if (top === 'video') return 'video';
  if (top === 'audio') return 'audio';
  return 'image';
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

interface MediaPickerProps {
  files: PickedFile[];
  onChange: (files: PickedFile[]) => void;
  onNotice?: (msg: string) => void;
}

export default function MediaPicker({ files, onChange, onNotice }: MediaPickerProps) {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const filesRef = useRef(files);
  filesRef.current = files;

  useEffect(() => () => {
    filesRef.current.forEach((f) => URL.revokeObjectURL(f.url));
  }, []);

  const addFiles = useCallback((picked: FileList | File[]): void => {
    const current = filesRef.current;
    const next = [...current];
    const warnings: string[] = [];
    let imageCount = current.filter((f) => f.kind === 'image').length;
    let videoCount = current.filter((f) => f.kind === 'video').length;
    for (const file of Array.from(picked)) {
      const kind = classify(file);
      if (kind === 'image' && imageCount >= IMG_LIMIT) {
        warnings.push(`Only ${IMG_LIMIT} images allowed — skipped ${file.name}.`);
        continue;
      }
      if (kind === 'video' && videoCount >= VIDEO_LIMIT) {
        warnings.push(`Only one video allowed — skipped ${file.name}.`);
        continue;
      }
      if (kind === 'image') imageCount += 1;
      if (kind === 'video') videoCount += 1;
      next.push({
        id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
        file,
        kind,
        url: URL.createObjectURL(file),
      });
    }
    onChange(next);
    if (warnings.length && onNotice) onNotice(warnings.join(' '));
  }, [onChange, onNotice]);

  function removeFile(id: string): void {
    const target = filesRef.current.find((f) => f.id === id);
    if (target) URL.revokeObjectURL(target.url);
    onChange(filesRef.current.filter((f) => f.id !== id));
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
  }

  const imageCount = files.filter((f) => f.kind === 'image').length;
  const videoCount = files.filter((f) => f.kind === 'video').length;

  return (
    <div className="media-picker">
      <div
        className={`drop-zone ${dragOver ? 'is-over' : ''} ${files.length ? 'has-files' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click(); }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*,video/*"
          multiple
          hidden
          onChange={(e) => { if (e.target.files) addFiles(e.target.files); e.target.value = ''; }}
        />
        <div className="drop-zone-icon" aria-hidden>↑</div>
        <div className="drop-zone-title">
          {dragOver ? 'Drop to attach' : 'Drag & drop images or video'}
        </div>
        <div className="drop-zone-sub">
          or <span className="drop-zone-link">click to browse</span> · up to {IMG_LIMIT} images or 1 video
        </div>
        <div className="drop-zone-counts">
          <span className={imageCount ? 'count-on' : ''}>{imageCount}/{IMG_LIMIT} images</span>
          <span className="dot">·</span>
          <span className={videoCount ? 'count-on' : ''}>{videoCount}/{VIDEO_LIMIT} video</span>
        </div>
      </div>

      {files.length > 0 && (
        <ul className="media-tiles">
          {files.map((f) => (
            <li key={f.id} className={`media-tile kind-${f.kind}`}>
              <div className="media-tile-thumb">
                {f.kind === 'image' && <img alt={f.file.name} src={f.url} />}
                {f.kind === 'video' && <video src={f.url} muted preload="metadata" />}
                {f.kind === 'audio' && <div className="media-tile-audio">audio</div>}
                <span className="media-tile-kind">{f.kind}</span>
                <button
                  type="button"
                  className="media-tile-remove"
                  aria-label={`Remove ${f.file.name}`}
                  onClick={(e) => { e.stopPropagation(); removeFile(f.id); }}
                >
                  ×
                </button>
              </div>
              <div className="media-tile-meta">
                <span className="media-tile-name" title={f.file.name}>{f.file.name}</span>
                <span className="media-tile-size">{formatBytes(f.file.size)}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
